import React, { useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import ConnectionFailedView from '../components/ConnectionFailedView';
import CreateAPotButton from '../components/CreateAPotButton';
import LoadingView from '../components/LoadingView';
import OnboardingFunnelController from '../components/OnboardingFunnelController';
import Pots from '../components/Pots';
import useStatus from '../hooks/useStatus';
import { selectShouldForceShowPotsOnb, selectShouldShowPotsList } from '../reducers/selectors';
const PotsScreen = ({ scrollViewRef, potsScrollHandler, scrollViewExtraStyle }) => {
    const { status } = useStatus();
    const shouldShowPotsOnb = useAppSelector(selectShouldForceShowPotsOnb);
    const isErrorInFetchingPotsAcc = useAppSelector((state) => state.unpersistedPotsReducer.isErrorInFetchingPotsAcc);
    const hasCompletedOnboardingFunnel = useAppSelector((state) => state.pots.hasCompletedOnboardingFunnel);
    const shouldShowPotsList = useAppSelector(selectShouldShowPotsList);
    const hasPostOpenInstructions = useAppSelector((state) => state.pots.hasPostOpenInstructions);
    const hasCompletedOnboardingFunnelRef = useRef(hasCompletedOnboardingFunnel);
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    useEffect(() => {
        if (hasCompletedOnboardingFunnel && !hasCompletedOnboardingFunnelRef.current) {
            if (scrollViewRef.current) {
                if ('scrollTo' in scrollViewRef.current) {
                    scrollViewRef.current?.scrollTo({ x: 0, y: 0 });
                }
                else if ('scrollToOffset' in scrollViewRef.current) {
                    scrollViewRef.current?.scrollToOffset({ animated: true, offset: 0 });
                }
            }
        }
        hasCompletedOnboardingFunnelRef.current = hasCompletedOnboardingFunnel;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCompletedOnboardingFunnel]);
    const content = useMemo(() => {
        if (status === undefined) {
            if (isErrorInFetchingPotsAcc) {
                return (<View style={paddingHorizontalStyle}>
            <ConnectionFailedView />
          </View>);
            }
            return (<View style={paddingHorizontalStyle}>
          <LoadingView />
        </View>);
        }
        return <OnboardingFunnelController />;
    }, [isErrorInFetchingPotsAcc, paddingHorizontalStyle, status]);
    const getStartedButton = useMemo(() => {
        if (status) {
            /**
             * We show create a pot button if
             * - status is NOT_STARTED
             * - forcefully show pots onboarding
             */
            if (status === 'NOT_STARTED' || (shouldShowPotsOnb && !hasPostOpenInstructions)) {
                return <CreateAPotButton />;
            }
        }
        return null;
    }, [hasPostOpenInstructions, shouldShowPotsOnb, status]);
    /**
     * We show pot list if
     * - user has ACTIVE pots account
     * - has active savings plan
     * - has ever had a successful collection if they ever had an autosave
     */
    if (shouldShowPotsList) {
        return (<View style={styles.container}>
        <Pots onScroll={potsScrollHandler} potsScrollViewRef={scrollViewRef}/>
      </View>);
    }
    return (<View style={styles.container}>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={potsScrollHandler} showsVerticalScrollIndicator={false} contentContainerStyle={scrollViewExtraStyle} ref={scrollViewRef}>
        {content}
      </Animated.ScrollView>
      {getStartedButton}
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
});
export default React.memo(PotsScreen);
